import i18n from "../../i18n";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
  Button,
  Group,
  Tabs,
  TextInput,
  Textarea,
  Select,
  Stack,
  Switch,
  Paper,
  NumberInput,
  Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabActionsOptional, notifyListRefresh } from "../../context/tabContext";
import { buildTabKey } from "../../utils/tabNavigation";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import McaStepBuilder from "../../components/mca/McaStepBuilder";
import McaSetValuesEditor from "../../components/mca/McaSetValuesEditor";
import McaWatchFieldsEditor, {
  watchFieldsToRows,
} from "../../components/mca/McaWatchFieldsEditor";
import RemoteSelect2 from "../../components/remoteSelect2";
import { buildMcaProcessPayload } from "../../helpers/mcaProcessPayload";
import {
  MCA_ENTITIES,
  MCA_ACTIONS,
  MCA_TRIGGER_TYPES,
  MCA_WATCH_FIELD_LOGIC,
  migrateStepNotifications,
  migrateStepFields,
  validateStepNotification,
} from "../../data/mcaEntities";
import { useMcaSubmitAction } from "../../hooks/useMcaSubmitAction";
import { McaEntityBanner } from "../../components/mca/McaEntityIndicators";
import { isEntityMcaLocked } from "../../components/mca/mcaApprovalLabels";
import { McaEditorReadOnlyContext } from "../../components/mca/mcaEditorReadOnly";

const apiEntity = "mcaProcess";
const MCA_PROCESS_LIST_PATH = "/management/mcaProcess";

const migrateFinalActionToSteps = (steps, finalAction) => {
  const hasStepSetValues = steps.some((s) => (s.setValues ?? []).length > 0);
  if (hasStepSetValues) return steps;

  const legacyStatus = finalAction?.setStatus?.trim();
  const legacyFields = finalAction?.setFields ?? {};
  if (!legacyStatus && !Object.keys(legacyFields).length) return steps;

  const migrated = steps.map((s) => ({
    ...s,
    setValues: (s.setValues ?? []).map((v) => ({ ...v })),
  }));
  const lastIdx = migrated.length - 1;
  if (lastIdx < 0) return migrated;

  const setValues = [...(migrated[lastIdx].setValues ?? [])];
  if (legacyStatus) setValues.push({ field: "status", value: legacyStatus });
  Object.entries(legacyFields).forEach(([field, value]) => {
    setValues.push({ field, value });
  });
  migrated[lastIdx] = { ...migrated[lastIdx], setValues };
  return migrated;
};

const getSaveErrorMessage = (api, res) => {
  if (typeof res?.error === "string") return res.error;
  const fromError = api.getErrorMsg(res?.error);
  if (fromError) return fromError;
  return res?.message || i18n.t("Save failed");
};

const defaultValues = {
  name: "",
  cname: "",
  description: "",
  status: "ACTIVE",
  priority: 0,
  triggerType: "HOOK",
  triggerConfig: {
    targetEntity: null,
    action: "UPDATE",
    watchFields: [],
    watchFieldLogic: "OR",
    allowedMakers: [],
    allowedMakerRoles: [],
  },
  steps: [
    {
      order: 1,
      name: "Review",
      approvalType: "ANY",
      approvers: [],
      approverRoles: [],
      departmentHead: false,
      checklist: [],
      setValues: [],
      notifications: [],
      stepFields: [],
    },
  ],
  finalAction: { setStatus: "", setFields: {} },
  onApproved: { setValues: [] },
  onTerminate: { setValues: [] },
  _id: null,
  mcaPending: false,
  mcaRequestId: null,
};

const McaProcessEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const [api] = useServerApi();
  const tabActions = useTabActionsOptional();
  const [activeTab, setActiveTab] = useState("basic");
  const [watchRows, setWatchRows] = useState(watchFieldsToRows([]));
  const [saving, setSaving] = useState(false);
  const [processRecord, setProcessRecord] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const form = useForm({ initialValues: defaultValues });
  const processLocked = isEntityMcaLocked(processRecord);

  const mcaSubmit = useMcaSubmitAction({
    api,
    apiEntity,
    mcaEntity: "approval",
    formMode: isNew ? "add" : "edit",
    entity: processRecord,
    isDirty: form.isDirty(),
    onSubmitted: async () => setReloadKey((value) => value + 1),
  });

  const syncTabLabel = (name) => {
    if (!tabActions?.enabled || !name?.trim()) return;
    tabActions.updateTabLabelByKey(
      buildTabKey(location.pathname, location.search),
      `MCA - ${name.trim()}`
    );
  };

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      try {
        const doc = await api.getById({ apiEntity, id });
        if (!doc) {
          showNotification({
            title: i18n.t("Process not found"),
            message: i18n.t(
              "This approval process no longer exists. Close this tab and open an existing process from the list, or create a new one."
            ),
            color: "red",
          });
          return;
        }
      const steps = migrateFinalActionToSteps(
        doc.steps?.length ? doc.steps : defaultValues.steps,
        doc.finalAction
      );
      const nextValues = {
        name: doc.name ?? "",
        cname: doc.cname ?? "",
        description: doc.description ?? "",
        status: doc.status ?? "ACTIVE",
        priority: doc.priority ?? 0,
        triggerType: doc.triggerType ?? "HOOK",
        triggerConfig: {
          targetEntity: doc.triggerConfig?.targetEntity ?? null,
          action: doc.triggerConfig?.action ?? "UPDATE",
          watchFields: doc.triggerConfig?.watchFields ?? [],
          watchFieldLogic: doc.triggerConfig?.watchFieldLogic ?? "OR",
          allowedMakers: doc.triggerConfig?.allowedMakers ?? [],
          allowedMakerRoles: doc.triggerConfig?.allowedMakerRoles ?? [],
        },
        steps: steps.map((s) => ({
          ...s,
          notifications: migrateStepNotifications(s),
          stepFields: migrateStepFields(s),
        })),
        finalAction: defaultValues.finalAction,
        onApproved: {
          setValues: (doc.onApproved?.setValues ?? []).map((v) => ({ ...v })),
        },
        onTerminate: {
          setValues: (doc.onTerminate?.setValues ?? []).map((v) => ({ ...v })),
        },
        _id: doc._id,
        mcaPending: !!doc.mcaPending,
        mcaRequestId: doc.mcaRequestId ?? null,
      };
      form.setValues(nextValues);
      form.resetDirty(nextValues);
      setProcessRecord(doc);
      setWatchRows(watchFieldsToRows(doc.triggerConfig?.watchFields));
      syncTabLabel(doc.name || doc.code);
      } catch (error) {
        showNotification({
          title: i18n.t("Error"),
          message: error?.message ?? i18n.t("Failed to load approval process"),
          color: "red",
        });
      }
    };
    load();
  }, [id, isNew, reloadKey]);

  useEffect(() => {
    if (isNew) return;
    syncTabLabel(form.values.name);
  }, [form.values.name, isNew, location.pathname, location.search]);

  useEffect(() => {
    if (activeTab === "watch" && form.values.triggerConfig.action !== "UPDATE") {
      setActiveTab("trigger");
    }
  }, [form.values.triggerConfig.action, activeTab]);

  const entityMeta = MCA_ENTITIES.find(
    (e) => e.value === form.values.triggerConfig.targetEntity
  );

  const save = async () => {
    if (processLocked) {
      showNotification({
        title: i18n.t("Changes blocked"),
        message: i18n.t(
          "This approval process is under approval and cannot be edited."
        ),
        color: "orange",
      });
      return;
    }
    if (!form.values.name?.trim()) {
      showNotification({
        title: i18n.t("Validation"),
        message: i18n.t("Process name is required"),
        color: "red",
      });
      setActiveTab("basic");
      return;
    }
    if (!form.values.triggerConfig.targetEntity) {
      showNotification({
        title: i18n.t("Validation"),
        message: i18n.t("Target entity is required"),
        color: "red",
      });
      setActiveTab("trigger");
      return;
    }
    if (form.values.steps.some((s) => !s.name?.trim())) {
      showNotification({
        title: i18n.t("Validation"),
        message: i18n.t("Each approval step needs a name"),
        color: "red",
      });
      setActiveTab("steps");
      return;
    }
    for (const step of form.values.steps) {
      for (const notification of step.notifications ?? []) {
        const error = validateStepNotification(notification);
        if (error) {
          showNotification({
            title: i18n.t("Validation"),
            message: `${step.name?.trim() || i18n.t("Step")}: ${error}`,
            color: "red",
          });
          setActiveTab("steps");
          return;
        }
      }
    }

    setSaving(true);
    try {
      const values = buildMcaProcessPayload(form.values, watchRows);
      const res = isNew
        ? await api.add({ apiEntity, values })
        : await api.updateById({ apiEntity, id, values });
      if (!res?.success) {
        throw new Error(getSaveErrorMessage(api, res));
      }
      showNotification({
        title: i18n.t("Saved"),
        message: i18n.t("Process saved"),
        color: "green",
      });
      notifyListRefresh(MCA_PROCESS_LIST_PATH);
      if (isNew) {
        const newId = res?.data?._id;
        if (newId) {
          navigate(`/management/mcaProcess/${newId}`, { replace: true });
        }
      } else {
        form.resetDirty(form.values);
        setReloadKey((value) => value + 1);
      }
    } catch (e) {
      showNotification({
        title: i18n.t("Error"),
        message: e?.message ?? i18n.t("Save failed"),
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate("/management/mcaProcess");
  };

  return (
    <Page
      title={isNew ? i18n.t("New Approval Process") : i18n.t("Edit Approval Process")}
      actions={
        <Group>
          <Button variant="default" onClick={handleClose}>{i18n.t("Close")}</Button>
          {mcaSubmit.showMcaSubmit &&
            (mcaSubmit.mcaSubmitDisabledReason ? (
              <Tooltip label={mcaSubmit.mcaSubmitDisabledReason} withArrow>
                <span>
                  <Button
                    color="green"
                    onClick={mcaSubmit.submitForApproval}
                    loading={mcaSubmit.submitting}
                    disabled={!mcaSubmit.canSubmitMcaCreate}
                  >{i18n.t("Submit for Approval")}</Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                color="green"
                onClick={mcaSubmit.submitForApproval}
                loading={mcaSubmit.submitting}
                disabled={!mcaSubmit.canSubmitMcaCreate}
              >{i18n.t("Submit for Approval")}</Button>
            ))}
          <Button onClick={save} loading={saving} disabled={processLocked}>{i18n.t("Save")}</Button>
        </Group>
      }
    >
      {processRecord && <McaEntityBanner entity={processRecord} />}
      <McaEditorReadOnlyContext.Provider value={processLocked}>
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="basic">{i18n.t("Basic Info")}</Tabs.Tab>
          <Tabs.Tab value="trigger">{i18n.t("Trigger Config")}</Tabs.Tab>
          <Tabs.Tab value="steps">{i18n.t("Approval Steps")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic" pt="xl">
          <Paper shadow="md" p="xl" radius={0}>
            <Stack>
              <TextInput
                label={i18n.t("Process name")}
                required
                {...form.getInputProps("name")}
                readOnly={processLocked}
              />
              <TextInput
                label={i18n.t("Chinese name")}
                {...form.getInputProps("cname")}
                readOnly={processLocked}
              />
              <Textarea
                label={i18n.t("Description")}
                {...form.getInputProps("description")}
                readOnly={processLocked}
              />
              <NumberInput
                label={i18n.t("Priority")}
                description={i18n.t(
                  "Higher priority processes are preferred when multiple match. Conditional processes always beat catch-all processes."
                )}
                min={0}
                {...form.getInputProps("priority")}
                readOnly={processLocked}
              />
              <Switch
                label={
                  form.values.status === "DFT"
                    ? i18n.t("Draft — submit for approval to activate")
                    : i18n.t("Active")
                }
                checked={form.values.status === "ACTIVE"}
                disabled={form.values.status === "DFT" || processLocked}
                onChange={(e) =>
                  form.setFieldValue(
                    "status",
                    e.currentTarget.checked ? "ACTIVE" : "INACTIVE"
                  )
                }
              />
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="trigger" pt="xl">
          <FormSectionCard title={i18n.t("Trigger Config")}>
            <Stack>
              <Select
                label={i18n.t("Trigger type")}
                data={MCA_TRIGGER_TYPES.map((item) => ({
                  ...item,
                  label: i18n.t(item.label),
                }))}
                {...form.getInputProps("triggerType")}
                readOnly={processLocked}
              />
              <Select
                label={i18n.t("Target entity")}
                data={MCA_ENTITIES.map((e) => ({ value: e.value, label: e.label }))}
                clearable
                required
                placeholder={i18n.t("Select entity")}
                {...form.getInputProps("triggerConfig.targetEntity")}
                onChange={(v) => {
                  form.setFieldValue("triggerConfig.targetEntity", v);
                  form.setFieldValue(
                    "steps",
                    (form.values.steps ?? []).map((step) => ({
                      ...step,
                      notifications: (step.notifications ?? []).map((n) => ({
                        ...n,
                        attachmentKeys: [],
                      })),
                    })),
                  );
                }}
                readOnly={processLocked}
              />
              <Select
                label={i18n.t("Action")}
                data={MCA_ACTIONS.map((item) => ({
                  ...item,
                  label: i18n.t(item.label),
                }))}
                {...form.getInputProps("triggerConfig.action")}
                readOnly={processLocked}
              />
              <RemoteSelect2
                form={form}
                name="triggerConfig.allowedMakerRoles"
                label={i18n.t("Trigger By (user role)")}
                description={i18n.t(
                  "Leave empty to trigger for any maker. If set, this flow runs only when the acting user has one of these roles."
                )}
                apiEntity="userRole"
                labelField="name"
                multiple
                clearable
                readOnly={processLocked}
                placeholder={i18n.t("All roles")}
                searchFields={["name"]}
              />
            </Stack>
          </FormSectionCard>
          {["CREATE", "UPDATE", "DELETE"].includes(form.values.triggerConfig.action) && (
            <FormSectionCard title={i18n.t("Conditions")}>
              <Select
                label={i18n.t("Condition logic")}
                data={MCA_WATCH_FIELD_LOGIC.map((item) => ({
                  ...item,
                  label: i18n.t(item.label),
                }))}
                mb="md"
                {...form.getInputProps("triggerConfig.watchFieldLogic")}
                readOnly={processLocked}
              />
              <McaWatchFieldsEditor
                rows={watchRows}
                onChange={setWatchRows}
                suggestedFields={entityMeta?.watchFields ?? []}
                entityKey={form.values.triggerConfig.targetEntity}
                action={form.values.triggerConfig.action}
              />
            </FormSectionCard>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="steps" pt="xl">
          {activeTab === "steps" && (
            <>
              <Paper shadow="md" p="xl" radius={0}>
                <McaStepBuilder
                  steps={form.values.steps}
                  onChange={(steps) => form.setFieldValue("steps", steps)}
                  statusFields={entityMeta?.setValueFields ?? []}
                  targetEntity={form.values.triggerConfig?.targetEntity}
                />
              </Paper>
              <FormSectionCard
                title={i18n.t("On Approved")}
                description={i18n.t(
                  "Applied to the target entity when this request is approved, including when routing skips the remaining steps. Leave empty to only apply the steps that actually ran."
                )}
                mt="xl"
              >
                <McaSetValuesEditor
                  setValues={form.values.onApproved?.setValues ?? []}
                  onChange={(setValues) =>
                    form.setFieldValue("onApproved.setValues", setValues)
                  }
                  statusFields={entityMeta?.setValueFields ?? []}
                  targetEntity={form.values.triggerConfig?.targetEntity}
                  steps={form.values.steps}
                  description=""
                />
              </FormSectionCard>
              <FormSectionCard
                title={i18n.t("On Terminate")}
                description={i18n.t(
                  "Applied to the target entity when this request is terminated. Leave empty to only close the request."
                )}
                mt="xl"
              >
                <McaSetValuesEditor
                  setValues={form.values.onTerminate?.setValues ?? []}
                  onChange={(setValues) =>
                    form.setFieldValue("onTerminate.setValues", setValues)
                  }
                  statusFields={entityMeta?.setValueFields ?? []}
                  targetEntity={form.values.triggerConfig?.targetEntity}
                  steps={form.values.steps}
                  description=""
                />
              </FormSectionCard>
            </>
          )}
        </Tabs.Panel>
      </Tabs>
      </McaEditorReadOnlyContext.Provider>
    </Page>
  );
};

export default McaProcessEditor;
